<template>
	<div class="event-calendar container"> <!-- Vuetify Calendar docs https://vuetifyjs.com/en/components/calendars -->
		<header class="event-calendar__controls">
			<div class="event-calendar__date-selector event-calendar__date-selector--desktop"
				:class="'event-calendar__data-selector--' + calendarSettings.type + '-view'">
				<div class="date-selector">
					<div class="date-selector__month-day">
						<button type="button" class="date-selector__month-prev" @click="prev" @keyup.space="prev">
							<span class="sr-only">Previous Month</span>
							<svg aria-hidden="true" version="1.1" class="icon-arrow-right"
								xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px"
								y="0px" viewBox="0 0 257.6 451.8" xml:space="preserve">
								<path
									d="M243.4,247.4L57.1,433.7c-11.9,11.9-31.1,11.9-42.9,0c-11.8-11.8-11.8-31.1,0-42.9L179,225.9L14.2,61.1 C2.3,49.2,2.3,30,14.2,18.2C26,6.3,45.2,6.3,57.1,18.2l186.3,186.3c5.9,5.9,8.9,13.7,8.9,21.4C252.3,233.7,249.3,241.5,243.4,247.4 z" />
							</svg>
						</button>
						<div class="date-selector__month" v-if="currentMonth">
							{{ currentMonth }}
							<span class="date-selector__date-number" v-if="calendarSettings.type === 'day'">{{
								currentDayOfMonth }}</span>
							<span class="date-selector__current-week" v-if="calendarSettings.type === 'week'">{{
								currentWeekRange }}</span>
						</div>
						<button type="button" class="date-selector__month-next" @click="next" @keyup.space="next">
							<span class="sr-only">Next Month</span>
							<svg aria-hidden="true" version="1.1" class="icon-arrow-right"
								xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px"
								y="0px" viewBox="0 0 257.6 451.8" xml:space="preserve">
								<path
									d="M243.4,247.4L57.1,433.7c-11.9,11.9-31.1,11.9-42.9,0c-11.8-11.8-11.8-31.1,0-42.9L179,225.9L14.2,61.1 C2.3,49.2,2.3,30,14.2,18.2C26,6.3,45.2,6.3,57.1,18.2l186.3,186.3c5.9,5.9,8.9,13.7,8.9,21.4C252.3,233.7,249.3,241.5,243.4,247.4 z" />
							</svg>
						</button>
					</div>
					<div class="date-selector__year">{{ currentYear }}</div>
				</div>
			</div>
			<div class="event-calendar__view-options">
				<div class="event-calendar__mobile-filter-toggle">
					<div class="fulton-dropdown">
						<button type="button" class="dropdown-toggle" @click="toggleFiltersDisplay">
							<span class="dropdown-title">{{ calendarSettings.labels.filterSelector }}</span>
							<svg aria-hidden="true" version="1.1" xmlns="http://www.w3.org/2000/svg"
								xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 433.4 247" xml:space="preserve">
								<path
									d="M195.8,234.8L14.9,53.9c-11.6-11.6-11.6-30.2,0-41.7c11.5-11.5,30.2-11.5,41.7,0l160.1,160l160-160 c11.6-11.6,30.2-11.6,41.7,0c11.6,11.5,11.6,30.1,0,41.7L237.5,234.8c-5.7,5.7-13.3,8.6-20.8,8.6 C209.1,243.5,201.6,240.5,195.8,234.8z">
								</path>
							</svg>
						</button>
					</div>
				</div>

				<div class="event-calendar__view-selector">
					<div class="fulton-dropdown">
						<button type="button" class="dropdown-toggle" aria-expanded="false"
							aria-controls="viewSelectorDropdown" id="btnViewSelector" @click="fultonDropdownToggle">
							<span class="dropdown-title">
								{{ calendarSettings.labels.viewSelector }}
							</span>
							<svg aria-hidden="true" version="1.1" xmlns="http://www.w3.org/2000/svg"
								xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 433.4 247" xml:space="preserve">
								<path
									d="M195.8,234.8L14.9,53.9c-11.6-11.6-11.6-30.2,0-41.7c11.5-11.5,30.2-11.5,41.7,0l160.1,160l160-160 c11.6-11.6,30.2-11.6,41.7,0c11.6,11.5,11.6,30.1,0,41.7L237.5,234.8c-5.7,5.7-13.3,8.6-20.8,8.6 C209.1,243.5,201.6,240.5,195.8,234.8z">
								</path>
							</svg>
						</button>
						<div class="dropdown-items" aria-labelledby="btnViewSelector"
							id="viewSelectorDropdown">
							<ul>
								<li>
									<button type="button" class="preventScroll" @click="setMonthView"
										@keyup.space="setMonthView">
										{{ calendarSettings.labels.month }}
										<span class="sr-only"> View</span>
									</button>
								</li>
								<li>
									<button type="button" class="preventScroll" @click="setWeekView"
										@keyup.space="setWeekView">
										{{ calendarSettings.labels.week }}
										<span class="sr-only"> View</span>
									</button>
								</li>
								<li>
									<button type="button" class="preventScroll" @click="setDayView"
										@keyup.space="setDayView">
										{{ calendarSettings.labels.day }}
										<span class="sr-only"> View</span>
									</button>
								</li>
							</ul>
						</div>
					</div>
				</div>
			</div>
		</header>
		<div class="event-calendar__main">
			<div class="event-calendar__filters filter-group" :class="{ 'active': mobileFiltersVisible }">

				<div class="filter-group__section" :key="section.filterName" v-for="section in filterFacetSections">
					<button type="button" :class="{ 'active': section.open }" class="filter-group__toggle"
						:aria-expanded="section.open" :aria-controls="formatToId('accordion', section.filterName)"
						:id="formatToId('btn', section.filterName)" @click="toggleFilterAccordion(section, $event)">
						<h3 class="filter-group__heading">
							{{ section.filterName }}
						</h3>
						<div class="filter-group__icon">
							<i aria-hidden="true"></i>
							<i aria-hidden="true"></i>
						</div>
					</button>

					<div :class="{ 'active': section.open }" class="filter-group__content"
						:aria-labelledby="formatToId('btn', section.filterName)"
						:id="formatToId('accordion', section.filterName)">
						<ul class="filter-group__list">
							<li class="filter-group__item" :key="filter.id" v-for="filter in section.subfilters">
								<label class="filter-group__label"
									:class="{ 'filter-group__label--checked': filter.active }" :for="filter.id">
									<input class="filter-group__checkbox" :id="filter.id" type="checkbox"
										@click="toggleCheck(filter)">
									<span class="filter-group__checkmark"></span>
									<span class="filter-group__text">{{ filter.displayName }}</span>
								</label>
							</li>
						</ul>
					</div>
				</div>

				<div class="filter-group__buttons">
					<button type="button" class="filter-group__button filter-group__button--apply"
						@click="applyFilters">
						{{ calendarSettings.labels.applyFiltersButton }}
						<span class="sr-only"> Filters</span>
					</button>
					<button type="button" class="filter-group__button filter-group__button--clear"
						@click="clearFilters">
						{{ calendarSettings.labels.clearFiltersButton }}
						<span class="sr-only"> Filters</span>
					</button>
				</div>

			</div>
			<div class="event-calendar__calendar"
				:class="'event-calendar__calendar--' + calendarSettings.type + '-view'">
				<div class="event-calendar__date-selector event-calendar__date-selector--mobile"
					:class="' event-calendar__data-selector--' + calendarSettings.type + '-view'">
					<div class="date-selector">
						<div class="date-selector__month-day">

							<button type="button" class="date-selector__month-prev" @click="prev">
								<span class="sr-only">Previous Month</span>
								<svg aria-hidden="true" version="1.1" class="icon-arrow-right"
									xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
									x="0px" y="0px" viewBox="0 0 257.6 451.8" xml:space="preserve">
									<path
										d="M243.4,247.4L57.1,433.7c-11.9,11.9-31.1,11.9-42.9,0c-11.8-11.8-11.8-31.1,0-42.9L179,225.9L14.2,61.1 C2.3,49.2,2.3,30,14.2,18.2C26,6.3,45.2,6.3,57.1,18.2l186.3,186.3c5.9,5.9,8.9,13.7,8.9,21.4C252.3,233.7,249.3,241.5,243.4,247.4 z" />
								</svg>
							</button>

							<div class="date-selector__month" v-if="currentMonth">
								{{ currentMonth }}
								<span class="date-selector__date-number" v-if="calendarSettings.type === 'day'">{{currentDayOfMonth }}</span>
								<span class="date-selector__current-week" v-if="calendarSettings.type === 'week'">{{currentWeekRange }}</span>
							</div>

							<button type="button" class="date-selector__month-next" @click="next">
								<span class="sr-only">Next Month</span>
								<svg aria-hidden="true" version="1.1" class="icon-arrow-right"
									xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
									x="0px" y="0px" viewBox="0 0 257.6 451.8" xml:space="preserve">
									<path
										d="M243.4,247.4L57.1,433.7c-11.9,11.9-31.1,11.9-42.9,0c-11.8-11.8-11.8-31.1,0-42.9L179,225.9L14.2,61.1 C2.3,49.2,2.3,30,14.2,18.2C26,6.3,45.2,6.3,57.1,18.2l186.3,186.3c5.9,5.9,8.9,13.7,8.9,21.4C252.3,233.7,249.3,241.5,243.4,247.4 z" />
								</svg>
							</button>
						</div>
						<div class="date-selector__year">{{ currentYear }}</div>
					</div>
				</div>

				<v-app class="vcal">
					<v-calendar ref="calendar" v-model="calendarSettings.start" v-on:change="changeEvent"
						:type="calendarSettings.type" :start="calendarSettings.start">
						<template v-slot:day="{ date }" :class="'day-class'">
							<div class="vcal__day-cell" @click="showEventsInMonthView(date, $event)">

								<button type="button" class="vcal__event-count" v-if="getEventsByDate(date).length"
									@click="showEventsInMonthView(date, $event)"
									@keyup.space="showEventsInMonthView(date, $event)">
									<span class="sr-only">Show Events</span>
									<span class="vcal__event-count-number">{{ getEventsByDate(date).length }}</span>
									<span class="vcal__event-count-label">{{ calendarSettings.eventCountLabel }}</span>
								</button>

								<div class="vcal__events" js-events v-if="getEventsByDate(date).length">
									<Event :event="event" :layout="'large'" :linkText="'Read More'" :key="event.Id"
										v-for="event in eventsMap[date]"></Event>

									<button type="button" class="cta-primary vcal__all-events-link" js-all-events-link @click="setDayView"
										@keyup.space="setDayView">
										{{ calendarSettings.labels.viewAllEvents }}
										<svg version="1.1" class="icon-arrow-right" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 257.6 451.8" xml:space="preserve">
										<path d="M243.4,247.4L57.1,433.7c-11.9,11.9-31.1,11.9-42.9,0c-11.8-11.8-11.8-31.1,0-42.9L179,225.9L14.2,61.1 C2.3,49.2,2.3,30,14.2,18.2C26,6.3,45.2,6.3,57.1,18.2l186.3,186.3c5.9,5.9,8.9,13.7,8.9,21.4C252.3,233.7,249.3,241.5,243.4,247.4 z" />
										</svg>
									</button>
								</div>

								<div class="vcal__day-events-close">
									<button type="button" class="vcal__day-events-close-button"
										@click="closeDayEventsInMonthlyView" @keyup.space="closeDayEventsInMonthlyView">
										<span class="sr-only">Close Events</span>
										<svg aria-hidden="true" class="icon-close" version="1.1"
											xmlns="http://www.w3.org/2000/svg"
											xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 212.982 212.982"
											xml:space="preserve">
											<path
												d="M131.804,106.491l75.936-75.936c6.99-6.99,6.99-18.323,0-25.312 c-6.99-6.99-18.322-6.99-25.312,0l-75.937,75.937L30.554,5.242c-6.99-6.99-18.322-6.99-25.312,0c-6.989,6.99-6.989,18.323,0,25.312 l75.937,75.936L5.242,182.427c-6.989,6.99-6.989,18.323,0,25.312c6.99,6.99,18.322,6.99,25.312,0l75.937-75.937l75.937,75.937 c6.989,6.99,18.322,6.99,25.312,0c6.99-6.99,6.99-18.322,0-25.312L131.804,106.491z" />
										</svg>
									</button>
								</div>
							</div>

						</template>

						<template v-slot:dayLabel="{ date }">
							{{ formatDate(date, 'YYYY-MM-DD', 'DD') }}
						</template>

						<!-- the events at the top (all-day) -->
						<template v-slot:dayHeader="{ date }">
							<div class="event-listing">
								<header class="event-listing__date-header">
									<div class="event-listing__day-and-month">
										<div class="event-listing__day">{{ formatDate(date, 'YYYY-MM-DD', 'DD') }}</div>
										<div class="event-listing__month">{{ formatDate(date, 'YYYY-MM-DD', 'MMM') }}</div>
									</div>
									<div class="event-listing__day-of-week">{{ formatDate(date, 'YYYY-MM-DD', 'dddd') }}</div>
								</header>
								<div class="event-listing__items">
									<div class="event-listing__no-events-message" v-if="!eventsMap[date]">{{calendarSettings.labels.noEventsMessage }}</div>
									<Event :event="event" :layout="'large'" :key="event.Id" v-for="event in eventsMap[date]"></Event>
								</div>
							</div>
						</template>
					</v-calendar>
				</v-app>

				<footer class='event-calendar__footer'>
					<button type="button" class="cta-primary event-calendar__date-range-prev" @click="prev">
						{{ 'Previous ' + calendarSettings.type }}
						<svg version="1.1" class="icon-arrow-right" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 257.6 451.8" xml:space="preserve">
							<path d="M243.4,247.4L57.1,433.7c-11.9,11.9-31.1,11.9-42.9,0c-11.8-11.8-11.8-31.1,0-42.9L179,225.9L14.2,61.1 C2.3,49.2,2.3,30,14.2,18.2C26,6.3,45.2,6.3,57.1,18.2l186.3,186.3c5.9,5.9,8.9,13.7,8.9,21.4C252.3,233.7,249.3,241.5,243.4,247.4 z" />
						</svg>
					</button>
					<button type="button" class="cta-primary event-calendar__date-range-next" @click="next">
						{{ 'Next ' + calendarSettings.type }}
						<svg version="1.1" class="icon-arrow-right" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 257.6 451.8" xml:space="preserve">
							<path d="M243.4,247.4L57.1,433.7c-11.9,11.9-31.1,11.9-42.9,0c-11.8-11.8-11.8-31.1,0-42.9L179,225.9L14.2,61.1 C2.3,49.2,2.3,30,14.2,18.2C26,6.3,45.2,6.3,57.1,18.2l186.3,186.3c5.9,5.9,8.9,13.7,8.9,21.4C252.3,233.7,249.3,241.5,243.4,247.4 z" />
						</svg>
					</button>
				</footer>
			</div>
		</div>
	</div>
</template>

<script>

import Vue from 'vue';
import Event from './Event.vue';
import PrimaryCTA from '../Global/PrimaryCTA.vue';
import axios from 'axios';

import Vuetify, {
	VApp, // required
	VCalendar
} from 'vuetify/lib';

Vue.use(Vuetify, {
	components: {
		VApp,
		VCalendar
	}
});
/* eslint-disable no-console */
/* eslint-disable no-undef */

export default {

	name: 'EventCalendar',

	components: {
		Event,
		PrimaryCTA
	},

	data() {

		return {

			calendarSettings: {
				eventCountLabel: 'Events Today',
				type: 'month',
				start: moment().format('YYYY-MM-DD'),
				typeOptions: [
					{ text: 'Day', value: 'day' },
					{ text: 'Week', value: 'week' },
					{ text: 'Month', value: 'month' },
					{ text: 'Custom Daily', value: 'custom-daily' },
					{ text: 'Custom Weekly', value: 'custom-weekly' }
				],
				labels: {
					day: 'Day',
					week: 'Week',
					month: 'Month',
					noEventsMessage: 'No scheduled events',
					viewAllEvents: 'View All Events',
					applyFiltersButton: 'Apply',
					clearFiltersButton: 'Clear All',
					viewSelector: 'View By',
					filterSelector: 'Filter By'
				}
			},

			filterFacetSections: [],
			events: [],
			appliedFilters: [],
			mobileFiltersVisible: true,
			currentMonth: moment().format('MMMM'),
			currentDayOfMonth: moment().format('D'),
			currentYear: moment().format('YYYY'),
			currentWeekRange: '',
			currentStartDate: moment().startOf('month').format('YYYY-MM-DD'),
			currentEndDate: moment().endOf('month').format('YYYY-MM-DD'),
			startingPath: ""
		}
	},

	computed: {

		eventsMap() {
			const map = {};
			this.events.forEach(e => (map[e.StartDate] = map[e.StartDate] || []).push(e));
			return map;
		},

		allEvents: function () { // consolidate events in a list indexed by their date
			return this.groupEventsByDate(this.events);
		},

		filteredEvents: function () {
			return this.groupEventsByDate(this.events);
		}
	},

	mounted: function () {

		let self = this;

		self.pageSetUpCall();
		let params = self.getUrlParameters(window.location.search + self.startingPath);

		if (params['startDate']) {
			self.currentStartDate = params['startDate'];
			self.calendarSettings.start = params['startDate'];
			self.$forceUpdate();
		}

		if (params['view']) {
			self.calendarSettings.type = params['view'];
		}

		self.$forceUpdate();

		// Set up click-outside handler for dropdown
		self.setupDropdownOutsideClick();

		// Mark Vue-managed dropdowns to avoid conflicts with vanilla JS dropdown system
		const vueDropdowns = self.$el.querySelectorAll('.fulton-dropdown button[aria-controls]');
		vueDropdowns.forEach(button => {
			button.setAttribute('data-dropdown-initialized', 'vue');
		});
	},

	beforeDestroy: function () {
		// Clean up event listeners
		if (this.removeDropdownClickListener) {
			this.removeDropdownClickListener();
		}
		if (this.removeDropdownKeyListener) {
			this.removeDropdownKeyListener();
		}
	},

	methods: {

		toggleFiltersDisplay: function () {
			this.mobileFiltersVisible = !this.mobileFiltersVisible;
		},

		// Backward compatibility - can be removed later
		closeViewSelectorDropdown: function () {
			utils.dropdown.close('btnViewSelector');
		},

		// Wrapper methods for backward compatibility
		closeFultonDropdown: function (buttonIdOrElement) {
			utils.dropdown.close(buttonIdOrElement);
		},

		closeAllFultonDropdowns: function () {
			utils.dropdown.closeAll();
		},

		setupDropdownOutsideClick: function () {
			// Use global utility handlers
			const handlers = utils.dropdown.setupGlobalHandlers();

			// Store cleanup functions for component destruction
			this.removeDropdownClickListener = handlers.removeClickListener;
			this.removeDropdownKeyListener = handlers.removeKeyListener;
		},


		initializeMobileView: function () {

			if (utils.mobile() || utils.tablet()) {

				this.closeAllFilterAccordions();
				this.mobileFiltersVisible = false;
			}
		},

		closeAllFilterAccordions: function () {

			this.filterFacetSections.forEach(function (section) {
				section.open = false;
			});
		},

		formatDate: function (date, inputFormat, outputFormat) {
			return moment(date, inputFormat).format(outputFormat);
		},

		next: function () {

			let self = this;
			self.closeDayEventsInMonthlyView();
			self.$refs.calendar.next();
		},

		prev: function () {

			let self = this;
			self.closeDayEventsInMonthlyView();
			self.$refs.calendar.prev();
		},
		nextKey: function (e) {

			if (e.key == " ") {
				self.next
			}

		},
		prevKey: function (e) {

			if (e.key == " ") {
				self.prev
			}

		},

		groupByArray: function (anArray, key) {
			return anArray.reduce(function (storage, item) {
				var group = item[key];
				storage[group] = storage[group] || [];
				storage[group].push(item);
				return storage;
			}, {});
		},

		groupEventsByDate: function (events) {

			let map = [];
			let self = this;

			map = self.groupByArray(events, 'StartDate');

			return map;
		},

		toggleCheck: function (filter) {

			filter.active = !filter.active;
			this.updateFilterList();
		},

		updateFilterList: function () {

			let self = this;
			self.appliedFilters = [];
			self.filterFacetSections.forEach(function (filterSection) {

				filterSection.subfilters.forEach(function (filter) {

					if (filter.active) {
						self.appliedFilters.push(filter);
					}
				});
			});
			self.$forceUpdate();
		},

		removeAllFilters: function () {

			let self = this;
			self.appliedFilters = [];
			self.filterFacetSections.forEach(function (filterSection) {

				filterSection.subfilters.forEach(function (filter) {

					filter.active = false;
				});
			});

		},

		generateFiltersQueryString: function () {

			let self = this;
			let filtersQuery = '';

			this.appliedFilters.forEach(function (filter, loopIndex) {
				filtersQuery += filter.id;
				if (self.appliedFilters.length > 1 && loopIndex !== self.appliedFilters.length - 1) {
					filtersQuery += '|';
				}
			});

			return filtersQuery;
		},

		clearURLParameters: function () {

			window.history.replaceState({}, document.title, window.location.pathname);
		},

		getUrlParameters: function (url) {

			if (!url) url = location.href;
			let question = url.indexOf('?');
			let hash = url.indexOf('#');
			if (hash == -1 && question == -1) return {};
			if (hash == -1) hash = url.length;
			let query = question == -1 || hash == question + 1 ? url.substring(hash) :
				url.substring(question + 1, hash);
			let result = {};

			query.split("&").forEach(function (part) {
				if (!part) return;
				part = part.split('+').join(' ');
				let eq = part.indexOf('=');
				let key = eq > -1 ? part.substr(0, eq) : part;
				let val = eq > -1 ? decodeURIComponent(part.substr(eq + 1)) : '';
				let from = key.indexOf('[');
				if (from == -1) result[decodeURIComponent(key)] = val;
				else {
					let to = key.indexOf(']', from);
					let index = decodeURIComponent(key.substring(from + 1, to));
					key = decodeURIComponent(key.substring(0, from));
					if (!result[key]) result[key] = [];
					if (!index) result[key].push(val);
					else result[key][index] = val;
				}
			});

			return result;
		},

		setUrlParameter: function (key, value) {

			key = encodeURI(key);
			value = encodeURI(value);
			var keyValuePair = document.location.search.substr(1).split('&');
			let i = keyValuePair.length;
			let x;

			while (i--) {
				x = keyValuePair[i].split('=');

				if (x[0] == key) {
					x[1] = value;
					keyValuePair[i] = x.join('=');
					break;
				}
			}

			if (i < 0) {
				keyValuePair[keyValuePair.length] = [key, value].join('=');
			}

			let newParameters = keyValuePair.join('&'); // remove first &
			if (newParameters.charAt(0) === '&') {
				newParameters = newParameters.substr(1);
			}

			let newURL = window.location.pathname + '?' + newParameters;
			window.history.replaceState({}, document.title, newURL);
		},

		toggleFilterAccordion: function (section, event) {

			section.open = !section.open;
			this.$forceUpdate();
		},

		fultonDropdownToggle: function (event) {
			utils.dropdown.toggle(event);
		},

		setWeekView: function () {
			this.calendarSettings.type = 'week';
			this.setUrlParameter('view', this.calendarSettings.type);
			this.closeViewSelectorDropdown();
		},

		setMonthView: function () {
			this.calendarSettings.type = 'month';
			this.setUrlParameter('view', this.calendarSettings.type);
			this.closeViewSelectorDropdown();
		},

		setDayView: function () {
			this.calendarSettings.type = 'day';
			this.setUrlParameter('view', this.calendarSettings.type);
			this.closeViewSelectorDropdown();
		},

		changeEvent: function (changeEvent) {

			let self = this;

			this.currentMonth = this.formatDate(changeEvent.start.month, 'M', 'MMMM');
			this.currentYear = changeEvent.start.year;
			this.currentDayOfMonth = changeEvent.start.day;

			let startDate = changeEvent.start.day;
			if (startDate.toString().length < 2) {
				startDate = this.formatDate(changeEvent.start.day, 'D', 'DD');
			}

			let endDate = changeEvent.end.day;
			if (endDate.toString().length < 2) {
				endDate = this.formatDate(changeEvent.end.day, 'D', 'DD');
			}

			if (parseInt(changeEvent.start.day) < parseInt(changeEvent.end.day)) {
				this.currentWeekRange = startDate + ' - ' + endDate;
			}
			else {
				this.currentWeekRange = startDate + ' - ' + this.formatDate(changeEvent.end.month, 'M', 'MMMM') + ' ' + endDate;
			}

			this.currentEndDate = changeEvent.end.date;
			this.setUrlParameter('endDate', this.currentEndDate);
			this.currentStartDate = changeEvent.start.date;
			this.setUrlParameter('startDate', this.currentStartDate);

			// Use real API call
			let eventService = window.location.origin + '/api/feature/events' + window.location.search + self.startingPath;
			axios.post(eventService).then(function (response) {
				self.events = response.data.Results;
				self.$forceUpdate();
			})
				.catch(function (error) {
					console.log(error);
				});
		},

		getEventsByDate(date) {

			let result = [];
			let dateString = date.toString();
			if (this.filteredEvents[dateString]) {
				result = this.filteredEvents[dateString];
			}

			return result;
		},

		showEventsInMonthView: function (date, clickEvent) {

			this.calendarSettings.start = date;

			let dayElement = clickEvent.target.closest('.v-calendar-weekly__day');
			let todaysEvents;
			if (dayElement.querySelector('[js-events]') && clickEvent.target != null) {

				todaysEvents = dayElement.querySelector('[js-events]').cloneNode(true);
				todaysEvents.classList.add('active');
			}
			else return;

			let weekElement = clickEvent.target.closest('.v-calendar-weekly__week');
			let isInCurrentMonth = !dayElement.classList.contains('.v-outside');
			let isCurrentlyOpen = dayElement.classList.contains('active');

			if (dayElement && weekElement && todaysEvents && isInCurrentMonth && !isCurrentlyOpen) {

				this.closeDayEventsInMonthlyView();
				let allEventsLink = todaysEvents.querySelector('[js-all-events-link]');
				allEventsLink.addEventListener('click', this.setDayView);

				dayElement.classList.add('active');
				weekElement.classList.add('active');

				let eventWrapper = document.createElement('div');
				eventWrapper.classList.add('event-calendar__monthly-event-listing');
				eventWrapper.appendChild(todaysEvents);

				utils.insertAfter(weekElement, eventWrapper);
			}
		},

		closeDayEventsInMonthlyView: function (event) {

			if (event) {
				event.stopPropagation();
			}

			let monthlyEventListings = document.querySelectorAll('.event-calendar__monthly-event-listing');
			monthlyEventListings.forEach(function (element) {// reset view
				//element.remove();
				element.parentNode.removeChild(element);//work with IE
			});
			let dayCells = document.querySelectorAll('.v-calendar-weekly__day, .v-calendar-weekly__week');
			dayCells.forEach(function (element) {
				element.classList.remove('active');
			});
		},

		clearFilters: function () {

			let self = this;
			self.removeAllFilters();
			self.clearURLParameters();

			// Use real API call
			let eventService = window.location.origin + '/api/feature/events' + window.location.search + "?" + self.startingPath;
			axios.post(eventService).then(function (response) {
				self.events = response.data.Results;
				self.$forceUpdate();
			})
				.catch(function (error) {
					console.log(error);
				});
		},

		applyFilters: function () {

			let self = this;
			self.closeDayEventsInMonthlyView();
			self.setUrlParameter('filters', self.generateFiltersQueryString());

			// Use real API call
			let eventService = window.location.origin + '/api/feature/events' + window.location.search + self.startingPath;
			axios.post(eventService).then(function (response) {
				self.events = response.data.Results;
				self.$forceUpdate();
			})
				.catch(function (error) {
					console.log(error);
				});
		},

		getDefaultEventJSON: function () {
			let self = this;

			// Use real API call
			let eventService = window.location.origin + '/api/feature/events?startDate=' + self.currentStartDate + '&endDate=' + self.currentEndDate + self.startingPath;
			axios.post(eventService).then(function (response) {
				self.events = response.data.Results;
				self.$forceUpdate();
			})
				.catch(function (error) {
					console.log(error);
				});
		},

		pageSetUpCall: function () {

			let self = this;

			// Use real API call
			var dataContext = window.context;
			let pageSetUpService = window.location.origin + '/api/feature/events/pagesetup?datasource=' + dataContext;
			axios.post(pageSetUpService).then(function (response) {
				self.calendarSettings.labels.day = response.data.data.DayLabel;
				self.calendarSettings.labels.week = response.data.data.WeekLabel;
				self.calendarSettings.labels.month = response.data.data.MonthLabel;
				self.calendarSettings.labels.viewAllEvents = response.data.data.EventsTodayLabel;
				if (response.data.data.StartingPath) {
					self.startingPath = "&startingPath=" + encodeURIComponent(response.data.data.StartingPath);
				}

				self.filterFacetSections = response.data.data.filters;

				let responseFilters = response.data.data.filters;

				responseFilters.forEach(function (section) {
					section.open = true;

					section.subfilters.forEach(function (filter) {
						filter.active = false;
					});
				});

				self.filterFacetSections = responseFilters;

				self.getDefaultEventJSON();
				self.initializeMobileView();
				self.$forceUpdate();
			})
				.catch(function (error) {
					console.log(error);
				});
		},

		formatToId(prefix, name) {
			if (!name) return '';
			return prefix + name.trim().replace(/\s+/g, '');
		}
	}
}

</script>